import { addDays, eachDay, makeDateKey, monthEnd, startOfWeek, type DateKey } from "../dates";
import type { AnswerValue, Entry, EntryStatus, MarkerTone, Scenario } from "../entities";
import { isEmptyAnswer } from "../reports/ReportBuilder";
import { ScenarioRunner } from "../scenario/ScenarioRunner";

export interface DayMark {
  subscriptionId: string;
  scenarioId: string;
  /** complete when anything was recorded that day (a draft's answers count, as in reports), skipped when the day was only skipped. */
  status: EntryStatus;
  marked: boolean;
  /** 0..1 when the scenario provides an intensity (the day's maximum), otherwise null. */
  intensity: number | null;
  /** Number of entries behind this mark (several attacks a day). */
  count: number;
  /** The highlight question was answered truthy in at least one entry (e.g. painkillers taken). */
  highlighted: boolean;
  tone: MarkerTone;
}

const truthy = (value: AnswerValue | undefined): boolean => {
  if (value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "number") return !Number.isNaN(value) && value > 0;
  if (typeof value === "string") return value !== "";
  return value === true;
};

export interface DayCell {
  date: DateKey;
  inMonth: boolean;
  isToday: boolean;
  isFuture: boolean;
  marks: DayMark[];
}

export interface MonthGrid {
  year: number;
  month: number;
  weeks: DayCell[][];
}

const normalise = (value: number, min: number, max: number) => (max === min ? 1 : (value - min) / (max - min));

export class CalendarModel {
  monthGrid(
    year: number, month: number, today: DateKey, entries: Entry[],
    scenarioOf: (subscriptionId: string) => Scenario | undefined,
  ): MonthGrid {
    const first = makeDateKey(year, month, 1);
    const last = monthEnd(first);
    const byDate = new Map<DateKey, Entry[]>();
    for (const entry of entries) {
      if (entry.deleted) continue;
      byDate.set(entry.date, [...(byDate.get(entry.date) ?? []), entry]);
    }
    const weeks: DayCell[][] = [];
    let week: DayCell[] = [];
    for (const date of eachDay(startOfWeek(first), addDays(startOfWeek(last), 6))) {
      const marks = this.dayMarks(byDate.get(date) ?? [], scenarioOf);
      week.push({ date, inMonth: date >= first && date <= last, isToday: date === today, isFuture: date > today, marks });
      if (week.length === 7) {
        weeks.push(week);
        week = [];
      }
    }
    return { year, month, weeks };
  }

  /** One mark per subscription, merged over all entries of the day. */
  dayMarks(entries: Entry[], scenarioOf: (subscriptionId: string) => Scenario | undefined): DayMark[] {
    const merged = new Map<string, DayMark>();
    for (const entry of entries) {
      // Answers save one by one, so a draft left before the last question still records what it holds (Today, Day and
      // reports show it too). Only a draft opened and left without a single answer records nothing.
      if (entry.status === "partial" && Object.keys(entry.answers).length === 0) continue;
      const scenario = scenarioOf(entry.subscriptionId);
      if (!scenario) continue;
      const mark = this.mark(entry, scenario);
      const existing = merged.get(entry.subscriptionId);
      merged.set(entry.subscriptionId, existing ? this.merge(existing, mark) : mark);
    }
    return [...merged.values()];
  }

  private merge(a: DayMark, b: DayMark): DayMark {
    const intensities = [a.intensity, b.intensity].filter((v): v is number => v !== null);
    const statuses = [a.status, b.status];
    const status: EntryStatus = statuses.includes("complete") ? "complete" : "skipped";
    return {
      ...a,
      status,
      marked: a.marked || b.marked,
      highlighted: a.highlighted || b.highlighted,
      intensity: intensities.length ? Math.max(...intensities) : null,
      count: a.count + b.count,
    };
  }

  mark(entry: Entry, scenario: Scenario): DayMark {
    const base: DayMark = {
      subscriptionId: entry.subscriptionId, scenarioId: scenario.id, status: entry.status === "skipped" ? "skipped" : "complete", marked: false, intensity: null, count: 1,
      highlighted: false, tone: scenario.calendarMarker?.tone ?? "accent",
    };
    if (entry.status === "skipped") return base;
    const visible = new Set(new ScenarioRunner(scenario).visibleQuestions(entry.answers).map((q) => q.id));
    const marker = scenario.calendarMarker;
    if (!marker) return { ...base, marked: Object.keys(entry.answers).length > 0 };
    const question = scenario.questions.find((q) => q.id === marker.questionId);
    const value = visible.has(marker.questionId) ? entry.answers[marker.questionId] : undefined;
    let marked = false;
    let intensity: number | null = null;
    if (question && value !== undefined && !isEmptyAnswer(question, value)) {
      if (question.type === "yesno") marked = value === true;
      else if (question.type === "scale") {
        marked = true;
        intensity = normalise(value as number, question.min, question.max);
      } else marked = true;
    }
    const intensityQuestion = marker.intensityQuestionId
      ? scenario.questions.find((q) => q.id === marker.intensityQuestionId)
      : undefined;
    if (marked && intensityQuestion?.type === "scale" && visible.has(intensityQuestion.id)) {
      const raw = entry.answers[intensityQuestion.id];
      if (typeof raw === "number" && !Number.isNaN(raw)) intensity = normalise(raw, intensityQuestion.min, intensityQuestion.max);
    }
    const highlightId = marker.highlightQuestionId;
    const highlighted = marked && highlightId !== undefined && visible.has(highlightId) && truthy(entry.answers[highlightId]);
    return { ...base, marked, intensity, highlighted };
  }
}
